import { formatMoney } from "../lib/money";
import { keepChoicesDropdownVisible } from "./choices-keep-visible";
import { buildVariantMap, variantIdFor } from "./variant-map";

// --------------- Interfaces ---------------

interface ShopifyVariantMedia {
  id: number;
  position: number;
  alt: string | null;
}

interface ShopifyVariant {
  id: number;
  title: string;
  available: boolean;
  price: number;
  compare_at_price: number | null;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  options: string[];
  featured_media: ShopifyVariantMedia | null;
}

interface VariantOption {
  label: string;
  value: string;
}

interface FakeVariantInput extends HTMLInputElement {
  dataset: DOMStringMap & {
    price?: string;
    bundleOption?: string;
    variantAddonOption?: string;
  };
}

interface ShareButtonElement extends HTMLElement {
  updateUrl(url: string): void;
}

interface PickupAvailabilityElement extends HTMLElement {
  fetchAvailability(variantId: number): void;
}

interface MediaGalleryElement extends HTMLElement {
  setActiveMedia(mediaId: string, animate: boolean): void;
}

// --------------- VariantSelects ---------------

// Kit radio value the customer picked by hand, kept across kits-container re-renders
let userKitChoice: string | null = null;

document.addEventListener("change", (e) => {
  const radio = e.target as HTMLInputElement;
  if (e.isTrusted && radio.matches?.('.kit-card input[type="radio"]')) userKitChoice = radio.value;
});

function restoreUserKitChoice(kitsContainer: HTMLElement): void {
  if (userKitChoice === null) return;
  const radio = Array.from(kitsContainer.querySelectorAll<HTMLInputElement>('.kit-card input[type="radio"]')).find(
    (r) => r.value === userKitChoice,
  );
  if (radio) radio.checked = true;
}

class VariantSelects extends HTMLElement {
  protected options: string[] = [];
  protected currentVariant: ShopifyVariant | undefined;
  get selectedVariant(): ShopifyVariant | undefined { return this.currentVariant; }
  protected variantData: ShopifyVariant[] | null = null;
  protected variantChangeEvent: Event;

  constructor() {
    super();
    this.variantChangeEvent = new Event("variant-changed", { bubbles: true });
    this.addEventListener("change", this.onVariantChange.bind(this));
  }

  connectedCallback(): void {
    // A device-locked collection link can land here with `?variant=` pointing at a MagSafe
    // Ring combo that's out of stock. Liquid pre-checks that value (disabled) by default, and
    // everything else in this class only reacts to a 'change' event — so nothing corrects the
    // initial selection until the customer clicks something themselves. Simulate that click
    // once on connect so Add to Cart, price, and media reflect an in-stock choice right away.
    const magsafeFieldset = this.querySelector<HTMLElement>('[data-option-name="magsafe-ring"]');
    const checkedRadio = magsafeFieldset?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    const firstAvailable = magsafeFieldset?.querySelector<HTMLInputElement>('input[type="radio"]:not([disabled])');
    if (checkedRadio?.disabled && firstAvailable) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  onVariantChange(): void {
    this.dispatchEvent(this.variantChangeEvent);
    // When nested inside variant-handler, the handler reads all options (selects + radios)
    // and resolves the full variant. Proceeding here with partial options would incorrectly
    // call setUnavailable() and hide the price.
    if (this.closest('variant-handler')) return;
    this.updateOptions();
    this.updateMasterId();
    // Force-disable while the section re-renders below — except for a pre-order variant,
    // which must stay clickable throughout: renderProductInfo()'s fetch is what re-enables
    // it, but a network hiccup (or a race with another synthetic variant change, e.g. the
    // favPhone/favCaseType cookie sync in VariantSelectsCaseTypeFilter) can leave that fetch
    // unresolved, stranding the button disabled indefinitely with no visible error.
    if (this.isPreOrder()) {
      this.setPreOrderState(true);
    } else {
      this.toggleAddButton(true, "", false);
    }
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      // Clear any pre-order state left by the previous selection (warning banner,
      // hidden shipping block, pre-order FAQ trigger) — renderProductInfo(), the only
      // other place that calls setPreOrderState(false), never runs from this branch.
      this.setPreOrderState(false);
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
      return;
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
    this.updateShareUrl();
  }

  updateOptions(): void {
    this.options = Array.from(this.querySelectorAll<HTMLSelectElement>("select"), (select) => select.value);
  }

  updateMasterId(): void {
    const variants = this.getVariantData();

    this.currentVariant = variants.find((variant) => variant.options.every((opt, i) => this.options[i] === opt));

    // Fallback: match on first option only
    if (!this.currentVariant) {
      this.currentVariant = variants.find((variant) => variant.option1 === this.options[0]);
    }
  }

  /**
   * Swaps the first gallery image src/srcset with the selected variant's featured media.
   * Reads URLs from the VariantImages JSON injected by the visionneuse Liquid snippet.
   */
  updateMedia(): void {
    if (!this.currentVariant) return;

    const mediaGallery = (document.getElementById(`MediaGallery-${this.dataset.section}`) || document.querySelector("media-gallery")) as HTMLElement | null;
    if (!mediaGallery) return;

    let variantImages: Record<string, string> = {};
    try {
      const el = document.querySelector<HTMLElement>('[id^="VariantImages"]');
      variantImages = JSON.parse(el?.textContent || "{}");
    } catch {
      return;
    }

    const url = variantImages[String(this.currentVariant.id)];
    if (!url) return;

    const mainImg = mediaGallery.querySelector<HTMLImageElement>(".product__media-list li:first-of-type .product__media img");
    if (mainImg) {
      mainImg.srcset = [493, 600, 823, 990, 1206, 1646, 1946].map((w) => `${url}&width=${w} ${w}w`).join(", ");
      mainImg.src = `${url}&width=1946`;
      mainImg.alt = this.currentVariant.featured_media?.alt ?? "";
    }
    // Remettre is-active sur le premier slide
    const allSlides = mediaGallery.querySelectorAll<HTMLElement>("[data-media-id]");
    allSlides.forEach((s) => {
      s.classList.remove("is-active");
      s.removeAttribute("mobile-visible");
    });
    allSlides[0]?.classList.add("is-active");
    allSlides[0]?.setAttribute("mobile-visible", "");

    // Reset aria-current sur les thumbnails
    const gallerySectionId = mediaGallery.id.replace("MediaGallery-", "");
    mediaGallery.querySelectorAll<HTMLButtonElement>(".thumbnail-list__item button").forEach((btn) => {
      btn.removeAttribute("aria-current");
    });
    mediaGallery.querySelector<HTMLButtonElement>(`#Slide-Thumbnails-${gallerySectionId}-0 button`)?.setAttribute("aria-current", "true");

    const thumbImg = mediaGallery.querySelector<HTMLImageElement>(`#Slide-Thumbnails-${gallerySectionId}-0 img`);
    if (thumbImg) {
      thumbImg.srcset = [54, 74, 104, 208, 416].map((w) => `${url}&width=${w} ${w}w`).join(", ");
      thumbImg.src = `${url}&width=416`;
      thumbImg.alt = this.currentVariant.featured_media?.alt ?? "";
    }
  }

  /**
   * Updates the browser URL with the selected variant ID (no page reload).
   */
  updateURL(): void {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState({}, "", `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl(): void {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`) as ShareButtonElement | null;
    if (!shareButton || !this.currentVariant) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  /**
   * Syncs all product form hidden inputs with the selected variant ID.
   */
  updateVariantInput(): void {
    if (!this.currentVariant) return;
    const productForms = document.querySelectorAll<HTMLElement>(`#product-form-${this.dataset.section}, #product-form-installment, .product-form-addon, .case_type--container`);
    productForms.forEach((form) => {
      const input = form.querySelector<HTMLInputElement>('input[name="id"]');
      if (!input) return;
      input.value = String(this.currentVariant!.id);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    // Notify Shopify apps (e.g. Klaviyo BIS) that follow the Dawn variant:change convention.
    // preOrder/comingSoon ride along so klaviyo.ts can show its "notify me" trigger for a
    // coming_soon variant and suppress it for a pre-order one, which is already
    // purchasable (see isPreOrder()/isComingSoon()).
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: { variant: this.currentVariant, preOrder: this.isPreOrder(), comingSoon: this.isComingSoon() },
      })
    );
  }

  /**
   * Whether the selected variant's custom.not_release_yet metafield is true AND its
   * custom.release_date metafield is set — i.e. a launch date is known. Neither
   * metafield is part of the variant JSON parsed by getVariantData(), so this is
   * looked up from the dedicated PreOrderVariants id → boolean map rendered in
   * buy-buttons.liquid. Drives the Add to cart button's "Pre order" label/enabled
   * state in setPreOrderState().
   */
  isPreOrder(): boolean {
    return this.checkVariantFlag(`PreOrderVariants-${this.dataset.section}`);
  }

  /**
   * Whether the selected variant's custom.not_release_yet metafield is true but
   * custom.release_date is still blank — no launch date known yet. Looked up from the
   * dedicated ComingSoonVariants id → boolean map rendered in buy-buttons.liquid.
   * Drives the Add to cart button's forced-disabled "Coming Soon" state in
   * toggleAddButton().
   */
  isComingSoon(): boolean {
    return this.checkVariantFlag(`ComingSoonVariants-${this.dataset.section}`);
  }

  private checkVariantFlag(mapId: string): boolean {
    if (!this.currentVariant) return false;
    const map = document.getElementById(mapId);
    if (!map) return false;

    let variantFlags: Record<string, boolean> = {};
    try {
      variantFlags = JSON.parse(map.textContent || "{}");
    } catch {
      return false;
    }

    return Boolean(variantFlags[String(this.currentVariant.id)]);
  }

  /**
   * The resolved pre-order warning text for the selected variant — from the
   * PreOrderWarningText id → string map rendered in buy-buttons.liquid (already merges
   * block.settings.pre_order_warning_en/_fr with the {date} placeholder replaced by
   * custom.release_date). Differs per variant, since each pre-order variant can have
   * its own release date.
   */
  private getPreOrderWarningText(): string | null {
    if (!this.currentVariant) return null;
    const map = document.getElementById(`PreOrderWarningText-${this.dataset.section}`);
    if (!map) return null;

    let texts: Record<string, string> = {};
    try {
      texts = JSON.parse(map.textContent || "{}");
    } catch {
      return null;
    }

    return texts[String(this.currentVariant.id)] ?? null;
  }

  /**
   * The formatted custom.release_date for the selected variant — from the
   * PreOrderReleaseDates id → string map rendered in product-side-faq.liquid. Used to
   * keep the {date} placeholder inside a pre-order-only FAQ item's content ([data-preorder-
   * release-date]) in sync when switching between pre-order variants with different dates.
   */
  private getPreOrderReleaseDate(): string | null {
    if (!this.currentVariant) return null;
    const map = document.getElementById(`PreOrderReleaseDates-${this.dataset.section}`);
    if (!map) return null;

    let dates: Record<string, string> = {};
    try {
      dates = JSON.parse(map.textContent || "{}");
    } catch {
      return null;
    }

    return dates[String(this.currentVariant.id)] ?? null;
  }

  /**
   * The raw (unformatted) custom.release_date metafield value for the selected variant —
   * from the PreOrderReleaseDateValue id → string map rendered in buy-buttons.liquid. Feeds
   * the properties[Release date]/[Date de sortie] hidden input's value in setPreOrderState()
   * (each pre-order variant can have its own release date).
   */
  private getPreOrderReleaseDateRaw(): string | null {
    if (!this.currentVariant) return null;
    const map = document.getElementById(`PreOrderReleaseDateValue-${this.dataset.section}`);
    if (!map) return null;

    let dates: Record<string, string> = {};
    try {
      dates = JSON.parse(map.textContent || "{}");
    } catch {
      return null;
    }

    return dates[String(this.currentVariant.id)] ?? null;
  }

  updatePickupAvailability(): void {
    const pickUpAvailability = document.querySelector("pickup-availability") as PickupAvailabilityElement | null;
    if (!pickUpAvailability) return;

    if (this.currentVariant?.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  removeErrorMessage(): void {
    // Reserved for future product-form error handling
  }

  /**
   * Fetches the current section via Shopify Section Rendering API
   * (?sections=sectionId → JSON ~15 KB vs full page ~200 KB+) and updates
   * price, fake-variant blocks (Duo/Trio/MagSafe…) and case-type availability.
   */
  renderProductInfo(): void {
    if (!this.currentVariant) return;

    const sectionId = this.dataset.section ?? "";

    const fetchedVariantId = this.currentVariant.id;
    fetch(`${this.dataset.url}?variant=${fetchedVariantId}&sections=${sectionId}`)
      .then((res) => res.json() as Promise<Record<string, string>>)
      .then((sections) => {
        if (this.currentVariant?.id !== fetchedVariantId) return;
        const sectionHtml = sections[sectionId];
        if (!sectionHtml) return;

        const html = new DOMParser().parseFromString(sectionHtml, "text/html");
        const priceId = `price-${sectionId}`;

        // --- Price block ---
        const priceSource = html.getElementById(priceId);
        if (priceSource) {
          document.querySelectorAll<HTMLElement>(`#${priceId}`).forEach((dest) => {
            dest.innerHTML = priceSource.innerHTML;
            dest.classList.remove("visibility-hidden");
          });

          // Buy banner + mobile header live in separate sections — reuse parsed HTML
          const priceContainer = priceSource.querySelector(".price__container");
          if (priceContainer) {
            document.querySelectorAll<HTMLElement>(".section-product-buy-banner .price__container, .section-product-top-mobile .price__container").forEach((p) => {
              p.classList.remove("visibility-hidden");
              if (p.parentElement?.classList.contains("upsell-price")) return;
              p.innerHTML = priceContainer.innerHTML;
            });
          }
        } else {
          // Section has no price element (e.g. product-side) — update price displays directly from variant data
          const formatted = formatMoney(this.currentVariant!.price);
          document.querySelectorAll<HTMLElement>(".price__container .price-item--regular").forEach((el) => {
            if (!el.closest(".upsell-product-container, .upsell-product, [class*='upsell']")) {
              el.textContent = formatted;
            }
          });
        }

        // Always sync ATC button price from variant data
        const atcPriceEl = document.querySelector<HTMLElement>(".atc-button-price");
        if (atcPriceEl) atcPriceEl.textContent = formatMoney(this.currentVariant!.price);

        // --- Fake variant blocks ---
        const fakeSelector = ".false-variant-container";
        const fakeSources = Array.from(html.querySelectorAll<HTMLElement>(fakeSelector));
        document.querySelectorAll<HTMLElement>(fakeSelector).forEach((dest) => {
          const match = fakeSources.find((src) => src.id === dest.id);
          if (match) {
            dest.innerHTML = match.innerHTML;
            dest.classList.remove("--hidden");
            setupFakeVariantPricing();
          } else {
            dest.classList.add("--hidden");
          }
        });

        // --- Case type availability ---
        const caseTypeSelector = document.querySelector<HTMLElement>(".case_type--container:not([data-option-name='magsafe-ring'])");
        if (caseTypeSelector && this.currentVariant) {
          const inputs = caseTypeSelector.querySelectorAll<HTMLInputElement>('input[type="radio"]');
          inputs.forEach((input) => {
            let caseTypeValue = input.value;

            // Alias mapping: French/display labels → stored Shopify option values
            if (caseTypeValue === "Impact (Mat)") caseTypeValue = "Impact (Matte)";
            else if (caseTypeValue === "Impact (Lustré)") caseTypeValue = "Impact (Gloss)";

            // Use option-based matching instead of title matching so this works for
            // both 2-option (Device/CaseType) and 3-option (Device/CaseType/MagSafeRing) products.
            const isVariant = this.variantData?.some((v) =>
              v.option1 === this.currentVariant!.option1 && v.option2 === caseTypeValue
            );

            input.disabled = !isVariant;
          });

          // Auto-select first available radio if current is disabled
          const checkedInput = caseTypeSelector.querySelector<HTMLInputElement>('input[type="radio"]:checked');
          if (checkedInput?.disabled) {
            const firstEnabled = caseTypeSelector.querySelector<HTMLInputElement>('input[type="radio"]:not([disabled])');
            if (firstEnabled) firstEnabled.checked = true;
          }
        }

        if (this.isPreOrder()) {
          this.setPreOrderState(true);
        } else {
          this.setPreOrderState(false);
          if (this.isComingSoon()) {
            this.toggleAddButton(true, window.variantStrings?.comingSoon);
          } else {
            this.toggleAddButton(!this.currentVariant!.available, window.variantStrings?.soldOut);
          }
        }

        // --- Kits container (may be in a different section than main) ---
        const kitsEl = document.querySelector<HTMLElement>(".kits-container");
        if (kitsEl) {
          const newKitsInline = html.querySelector<HTMLElement>(".kits-container");
          if (newKitsInline) {
            // Kits are in the same section — already parsed
            restoreUserKitChoice(newKitsInline);
            kitsEl.replaceWith(newKitsInline);
            (window as any).__updateKitAtcPrice?.();
            document.dispatchEvent(new CustomEvent("kits:replaced"));
          } else {
            // Kits are in a separate section — find its ID from the DOM wrapper
            const kitsSectionId = kitsEl.closest<HTMLElement>('[id^="shopify-section-"]')?.id.replace("shopify-section-", "");
            if (kitsSectionId) {
              fetch(`${this.dataset.url}?variant=${this.currentVariant!.id}&sections=${kitsSectionId}`)
                .then((r) => r.json() as Promise<Record<string, string>>)
                .then((ks) => {
                  const kHtml = ks[kitsSectionId];
                  if (!kHtml) return;
                  const kParsed = new DOMParser().parseFromString(kHtml, "text/html");
                  const newKits = kParsed.querySelector<HTMLElement>(".kits-container");
                  if (newKits) {
                    restoreUserKitChoice(newKits);
                    kitsEl.replaceWith(newKits);
                    (window as any).__updateKitAtcPrice?.();
                    document.dispatchEvent(new CustomEvent("kits:replaced"));
                  }
                });
            }
          }
        }
      })
      .catch((err) => {
        // Without this, a network error/timeout left onVariantChange()'s earlier
        // toggleAddButton(true, ...) permanently in place — the button stayed
        // disabled with no visible error. Fall back to the same state this method
        // would have set from the fetch, using data already available synchronously.
        console.error("[renderProductInfo] fetch failed, falling back to known variant state:", err);
        if (this.currentVariant?.id !== fetchedVariantId) return;
        if (this.isPreOrder()) {
          this.setPreOrderState(true);
        } else {
          this.setPreOrderState(false);
          if (this.isComingSoon()) {
            this.toggleAddButton(true, window.variantStrings?.comingSoon);
          } else {
            this.toggleAddButton(!this.currentVariant!.available, window.variantStrings?.soldOut);
          }
        }
      });
  }

  /**
   * Enables/disables all add-to-cart buttons and updates their label.
   */
  toggleAddButton(disable = true, text = "", modifyClass = true): void {
    const productForms = document.querySelectorAll<HTMLElement>(`#product-form-${this.dataset.section}, #product-form-installment`);
    if (!productForms.length) return;

    // product-static-buy / product-info sections use data-add-to-cart-button
    const addButtons = document.querySelectorAll<HTMLButtonElement>("[data-add-to-cart-button]");
    const addButtonTexts = document.querySelectorAll<HTMLElement>("[data-add-to-cart-button] > span");
    if (addButtons.length) {
      if (disable) {
        addButtons.forEach((btn) => btn.setAttribute("disabled", "disabled"));
        if (text) addButtonTexts.forEach((el) => (el.textContent = text));
      } else {
        addButtons.forEach((btn) => btn.removeAttribute("disabled"));
        addButtonTexts.forEach((el) => (el.textContent = window.variantStrings?.addToCart ?? ""));
      }
    }

    // buy-buttons.liquid submit button has no data-add-to-cart-button; update it directly via the form
    productForms.forEach((form) => {
      const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"][name="add"]');
      if (!submitBtn) return;
      const label = submitBtn.querySelector<HTMLElement>(".product-form__submit-label");
      const textNode = label ? Array.from(label.childNodes).find((n) => n.nodeType === Node.TEXT_NODE) : null;
      if (disable) {
        submitBtn.disabled = true;
        if (text && textNode) textNode.textContent = `${text} – `;
      } else {
        submitBtn.disabled = false;
        if (textNode) textNode.textContent = `${window.variantStrings?.addToCart ?? ""} – `;
      }
    });
  }

  /**
   * Toggles the pre-order state for the selected variant (custom.not_release_yet=true):
   * every Add to cart button on the page stays enabled but relabeled "Pre order", and
   * the warning banner rendered above each of them (data-pre-order-warning) is shown,
   * so the customer knows the whole order will be held until the item arrives.
   */
  setPreOrderState(active: boolean): void {
    document.querySelectorAll<HTMLElement>("[data-pre-order-warning]").forEach((el) => {
      el.hidden = !active;
    });
    // Hidden properties[Release date]/[Date de sortie] input read by the Checkout Blocks
    // app — disabled inputs are excluded from FormData, so this keeps it out of the
    // /cart/add.js payload unless the selected variant is actually pre-order. Its value is
    // set below (once active, from getPreOrderReleaseDateRaw()) since it differs per variant.
    document.querySelectorAll<HTMLInputElement>("[data-pre-order-property]").forEach((el) => {
      el.disabled = !active;
    });
    // shipping-content (product-side-shipping.liquid): a pre-order item has no shipping
    // estimate to show, so hide it — inverse of the warning banner above.
    document.querySelectorAll<HTMLElement>("[data-hide-when-pre-order]").forEach((el) => {
      el.hidden = active;
    });
    // FAQ trigger buttons for a pre-order-only FAQ item (product-side-faq.liquid,
    // faq_ref_preorder setting): only shown while the selected variant is pre-order.
    document.querySelectorAll<HTMLElement>("[data-show-when-pre-order]").forEach((el) => {
      el.hidden = !active;
    });
    if (!active) return;

    // Keep the {date} placeholder inside that FAQ item's content in sync — a product
    // can have several pre-order variants with different release dates.
    const releaseDate = this.getPreOrderReleaseDate();
    if (releaseDate) {
      document.querySelectorAll<HTMLElement>("[data-preorder-release-date]").forEach((el) => {
        el.textContent = releaseDate;
      });
    }

    const rawReleaseDate = this.getPreOrderReleaseDateRaw();
    if (rawReleaseDate) {
      document.querySelectorAll<HTMLInputElement>("[data-pre-order-property]").forEach((el) => {
        el.value = rawReleaseDate;
      });
    }

    const warningText = this.getPreOrderWarningText();
    if (warningText) {
      document.querySelectorAll<HTMLElement>("[data-pre-order-warning]").forEach((el) => {
        el.textContent = warningText;
      });
    }

    const text = window.variantStrings?.preOrder ?? "";

    // product-info.liquid / product-static-buy.liquid / featured-product.liquid buttons
    document.querySelectorAll<HTMLButtonElement>("[data-add-to-cart-button]").forEach((btn) => {
      btn.removeAttribute("disabled");
    });
    document.querySelectorAll<HTMLElement>("[data-add-to-cart-button] > span").forEach((el) => {
      el.textContent = text;
    });

    // buy-buttons.liquid submit button has no data-add-to-cart-button; update it via the form
    const productForms = document.querySelectorAll<HTMLElement>(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((form) => {
      const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"][name="add"]');
      if (!submitBtn) return;
      submitBtn.disabled = false;
      const label = submitBtn.querySelector<HTMLElement>(".product-form__submit-label");
      const textNode = label ? Array.from(label.childNodes).find((n) => n.nodeType === Node.TEXT_NODE) : null;
      if (textNode) textNode.textContent = `${text} – `;
    });
  }

  setUnavailable(): void {
    const addButtons = document.querySelectorAll<HTMLButtonElement>("[data-add-to-cart-button]");
    const addButtonTexts = document.querySelectorAll<HTMLElement>("[data-add-to-cart-button] > span");
    const price = document.getElementById(`price-${this.dataset.section}`);

    if (!addButtons.length) return;
    addButtonTexts.forEach((el) => (el.textContent = window.variantStrings?.unavailable ?? ""));
    addButtons.forEach((btn) => (btn.disabled = true));
    price?.classList.add("visibility-hidden");
  }

  getVariantData(): ShopifyVariant[] {
    if (!this.variantData) {
      const script = this.querySelector<HTMLScriptElement>('[type="application/json"]');
      this.variantData = JSON.parse(script?.textContent ?? "[]");
    }
    return this.variantData!;
  }
}
customElements.define("variant-selects", VariantSelects);

// ============================================================
// Fake Variant Price Management
// ============================================================

let originalVariantPrice: number | null = null;

/**
 * Attaches change listeners to all fake-variant and addon inputs.
 * Re-called after DOM updates from renderProductInfo.
 */
function setupFakeVariantPricing(): void {
  const inputs = document.querySelectorAll<FakeVariantInput>('.false-variant-container input[type="radio"], .false-variant-container input[type="checkbox"], .variant-addon-container input[data-variant-addon-option="true"]');
  inputs.forEach((input) => {
    input.addEventListener("change", handleFakeVariantPriceChange);
  });
}

function handleFakeVariantPriceChange(event: Event): void {
  updateProductPricesWithFakeVariant(event.target as FakeVariantInput);
}

/**
 * Recomputes total price (base variant + all selected add-ons) and updates displays.
 */
function updateProductPricesWithFakeVariant(selectedInput: FakeVariantInput): void {
  if (originalVariantPrice === null) {
    originalVariantPrice = resolveBaseVariantPrice();
  }

  const totalFakePrice = calculateAllSelectedFakeVariantPrices();
  updateAllPriceDisplays(originalVariantPrice + totalFakePrice);
}

/** Resolves the base variant price in cents from the best available source. */
function resolveBaseVariantPrice(): number {
  const variantSelects = document.querySelector("variant-selects") as VariantSelects | null;

  if (variantSelects?.selectedVariant?.price) {
    return variantSelects.selectedVariant.price;
  }

  if (variantSelects) {
    const variantData = variantSelects.getVariantData();
    const selectedOptions = Array.from(variantSelects.querySelectorAll<HTMLSelectElement>("select"), (s) => s.value);
    const match = variantData.find((v) => v.options.every((opt, i) => selectedOptions[i] === opt));
    if (match?.price) return match.price;
  }

  // DOM fallback: parse displayed price text
  const priceText = document.querySelector(".price__container .price-item--regular")?.textContent?.replace(/[^0-9.,]/g, "");
  if (priceText) return Math.round(parseFloat(priceText) * 100);

  return 0;
}

/** Sums up the prices of all currently selected fake-variant and addon inputs. */
function calculateAllSelectedFakeVariantPrices(): number {
  let total = 0;

  document.querySelectorAll<FakeVariantInput>('.false-variant-container input[data-bundle-option="true"]:checked').forEach((input) => {
    if (input.dataset.price) total += parseInt(input.dataset.price, 10);
  });

  document.querySelectorAll<FakeVariantInput>('.variant-addon-container input[data-variant-addon-option="true"]:checked').forEach((input) => {
    if (input.dataset.price && input.value !== "null") {
      total += parseInt(input.dataset.price, 10);
    }
  });

  return total;
}

/**
 * Updates all non-upsell price displays on the page with the new formatted price.
 * Also updates the ATC button price when no non-base kit is active (kit-atc-override handles the kit case).
 */
function updateAllPriceDisplays(newPrice: number): void {
  const formatted = formatMoney(newPrice);
  document.querySelectorAll<HTMLElement>(".price__container .price-item--regular").forEach((el) => {
    if (!el.closest('.upsell-product-container, .upsell-product, [class*="upsell"]')) {
      el.textContent = formatted;
    }
  });
  const checkedKitRadio = document.querySelector<HTMLInputElement>('.kit-card input[type="radio"]:checked');
  if (!checkedKitRadio || checkedKitRadio.value === 'base') {
    const atcPriceEl = document.querySelector<HTMLElement>('.atc-button-price');
    if (atcPriceEl) atcPriceEl.textContent = formatted;
  }
}

document.addEventListener("DOMContentLoaded", () => setupFakeVariantPricing());
setupFakeVariantPricing();

// ============================================================
// VariantRadios
// ============================================================

const INSPIRATION_OPTION_MAP: Record<string, string> = {
  "Full Size Photo": "full",
  "Frame Music Player": "frame",
  "Simple Music Player": "simple",
  "Album Music Player": "album",
  "Mosaic 1": "mosaicUn",
  "Mosaic 2": "mosaicDeux",
  "Square Photo": "square",
  "Taped Photo": "tapedPhoto",
  "Taped Polaroid": "tapedPolaroid",
  "Taped Polaroid on Mosaic": "tapedPolaroidMosaic",
};

class VariantRadios extends VariantSelects {
  onVariantChange(): void {
    this.dispatchEvent(this.variantChangeEvent);
    // Same guard as VariantSelects: let variant-handler resolve the full variant
    // when this element is one of its nested pickers.
    if (this.closest('variant-handler')) return;
    this.updateOptions();
    this.updateMasterId();
    // See VariantSelects.onVariantChange(): a pre-order variant must stay clickable
    // instead of being force-disabled pending renderProductInfo()'s fetch.
    if (this.isPreOrder()) {
      this.setPreOrderState(true);
    } else {
      this.toggleAddButton(true, "", false);
    }
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      // Clear any pre-order state left by the previous selection (warning banner,
      // hidden shipping block, pre-order FAQ trigger) — renderProductInfo(), the only
      // other place that calls setPreOrderState(false), never runs from this branch.
      this.setPreOrderState(false);
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
      return;
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
    this.updateShareUrl();

    if (document.querySelector(".--impact")) {
      this.updateInspiration();
    }
  }

  updateOptions(): void {
    this.options = Array.from(this.querySelectorAll<HTMLFieldSetElement>("fieldset"), (fieldset) => Array.from(fieldset.querySelectorAll<HTMLInputElement>("input")).find((radio) => radio.checked)?.value ?? "");
  }

  updateMedia(): void {
    // Only applies to chargepod products
    if (!this.classList.contains("--chargepods")) return;
    super.updateMedia();
  }

  /**
   * Rebuilds the inspiration gallery grid based on the selected Impact product option.
   */
  updateInspiration(): void {
    const option = this.currentVariant?.option1;
    if (!option) return;

    const list = document.querySelector<HTMLElement>(".inspiration-blocks.--impact .inspiration-grid");
    if (!list) return;

    const dataKey = INSPIRATION_OPTION_MAP[option] ?? "full";
    const photoList = list.dataset[dataKey] ?? list.dataset["full"] ?? "";
    const images = photoList.split("|").filter(Boolean);

    const domain = "//www.kasemedesign.ca/cdn/shop/";
    list.innerHTML = images
      .map(
        (image, i) => `
          <li>
            <picture>
              <source media="(min-width:521px)" srcset="${domain}${image}?crop=center&height=380&width=380 1x,${domain}${image}?crop=center&height=760&width=760 2x">
              <source media="(max-width:520px)" srcset="${domain}${image}?crop=center&height=200&width=200 1x,${domain}${image}?crop=center&height=400&width=400 2x">
              <img src="${domain}${image}?crop=center&height=200&width=200" alt="Inspiration ${i + 1}" width="380" height="380" lazyload="true">
            </picture>
          </li>`,
      )
      .join("");
  }
}
customElements.define("variant-radios", VariantRadios);

// ============================================================
// VariantHandler — with upsell + rewards support
// ============================================================

class VariantHandler extends VariantSelects {
  private variantOrder: string | undefined;
  private upsellContainer: HTMLElement | null;

  constructor() {
    super();
    this.addEventListener("variant-changed", this.onVariantChange.bind(this));
    this.variantOrder = this.dataset.variantOrder;
    this.upsellContainer = document.querySelector(".upsell-product-container");

    this.initChoices();
    this.initDialog();
  }

  /** Initialises Choices.js dropdowns for all product-form selects. */
  private initChoices(): void {
    this.querySelectorAll<HTMLSelectElement>(".product-form__input select").forEach(
      (select) =>
        {
          new Choices(select, {
            shouldSort: false,
            searchResultLimit: 100,
            itemSelectText: "",
            position: "bottom",
          });
          keepChoicesDropdownVisible(select);
        },
    );
  }

  private initDialog(): void {
    const openBtn = this.querySelector<HTMLElement>("[data-toggle-phone-model]");
    if (!openBtn) return;

    const dialog = this.querySelector<HTMLDialogElement>(".dialog");
    const closeBtn = this.querySelector<HTMLElement>(".dialog [data-dialog-close]");
    if (!dialog || !closeBtn) return;

    openBtn.addEventListener("click", () => {
      document.body.classList.add("--no-scroll");
      dialog.showModal();
    });

    closeBtn.addEventListener("click", () => {
      document.body.classList.remove("--no-scroll");
      dialog.close();
    });
  }

  onVariantChange(): void {
    this.updateOptions();
    this.updateMasterId();
    // See VariantSelects.onVariantChange(): a pre-order variant must stay clickable
    // instead of being force-disabled pending renderProductInfo()'s fetch.
    if (this.isPreOrder()) {
      this.setPreOrderState(true);
    } else {
      this.toggleAddButton(true, "", false);
    }
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      // Clear any pre-order state left by the previous selection (warning banner,
      // hidden shipping block, pre-order FAQ trigger) — renderProductInfo(), the only
      // other place that calls setPreOrderState(false), never runs from this branch.
      this.setPreOrderState(false);
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
      return;
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
    this.updateShareUrl();

    if (this.upsellContainer) this.updateUpsell();
    this.updateRewardPoints(this.currentVariant.price);
  }

  updateOptions(): void {
    const fromRadios = Array.from(this.querySelectorAll<HTMLFieldSetElement>("fieldset"), (fieldset) => Array.from(fieldset.querySelectorAll<HTMLInputElement>("input")).find((r) => r.checked)?.value ?? "");
    const fromSelects = Array.from(this.querySelectorAll<HTMLSelectElement>("select"), (s) => s.value);

    const combined = this.variantOrder === "mixed-reverse" ? [...fromSelects, ...fromRadios] : [...fromRadios, ...fromSelects];

    this.options = combined;
  }

  updateMasterId(): void {
    this.currentVariant = this.getVariantData().find((variant) => variant.options.every((opt, i) => this.options[i] === opt));
  }

  getVariantData(): ShopifyVariant[] {
    if (!this.variantData) {
      const script = this.querySelector<HTMLScriptElement>('[type="application/json"]');
      this.variantData = JSON.parse(script?.textContent ?? "[]");
    }
    return this.variantData!;
  }

  /**
   * Calculates and displays KM reward points for the given variant price (in cents).
   */
  private updateRewardPoints(price: number): void {
    const mention = document.querySelector<HTMLElement>(".km-reward-mention");
    if (!mention) return;

    let points = price / 100;
    if (String(price % 100).padStart(2, "0") !== "00") points += 1;
    points = Math.trunc(points) * 10;

    const em = mention.querySelector("em");
    if (em) em.textContent = `${points} points`;
  }

  /**
   * Updates upsell products based on the currently selected phone model.
   */
  updateUpsell(): void {
    this.upsellContainer?.querySelectorAll<HTMLElement>(".upsell-product").forEach((element) => {
      const upsellOptions = element.querySelector<HTMLSelectElement>('[name="product-options"]');
      if (!upsellOptions) return;

      const type = element.getAttribute("data-type");

      if (type === "Screen Protector" || type === "Vitre Protectrice") {
        let isMatching = false;
        upsellOptions.querySelectorAll<HTMLOptionElement>("option").forEach((option) => {
          const phoneModels = option.getAttribute("data-phone-model")?.split("|") ?? [];
          if (phoneModels.includes(this.options[0])) {
            isMatching = true;
            option.selected = true;
            upsellOptions.dispatchEvent(new Event("change"));
          }
        });

        const errorMessage = element.querySelector<HTMLElement>(".error-message");
        if (errorMessage) {
          errorMessage.style.display = isMatching ? "none" : "block";
        }
      }
    });
  }
}
customElements.define("variant-handler", VariantHandler);

// ============================================================
// VariantSelectsCaseTypeFilter — phone case type filtering
// ============================================================

class VariantSelectsCaseTypeFilter extends VariantSelects {
  constructor() {
    super();
    // listenToChanges() must run first: initSelects() dispatches a synthetic
    // change event (cookie-driven device override) that onPhoneVariantChange
    // relies on to re-disable Case Type buttons for the new device — if that
    // listener isn't attached yet, the event fires into a void.
    this.listenToChanges();
    this.initSelects();
    this.updateCaseTypeOptions(this.getVariantOptions(), true);
    // Apply the Kobo swap on load — landing directly on a Kobo variant via a
    // ?variant= URL never fires a change event.
    this.swapKoboImages();
  }

  /** Initialises Choices.js on all selects and pre-selects favPhone + favCaseType from cookies. */
  private initSelects(): void {
    // Decode the cookie value — design-collections-fetch.ts stores it raw (spaces),
    // but onPhoneVariantChange stores it URL-encoded. Handle both.
    const rawFavPhone = this.getCookie("favPhone");
    let favPhone = "iPhone 17";
    if (rawFavPhone) {
      try { favPhone = decodeURIComponent(rawFavPhone) || "iPhone 17"; } catch { favPhone = rawFavPhone; }
    }

    let deviceChanged = false;

    this.querySelectorAll<HTMLSelectElement>("select").forEach((select) => {
      const isDevice = select.name.includes("Device") || select.name.includes("Modèle");
      if (isDevice) {
        const exists = Array.from(select.options).some((o) => o.value === favPhone);
        if (exists) {
          // Track whether cookie overrides the server-rendered variant selection.
          // If yes, we need to re-trigger a full variant change so kits and input[name=id] sync.
          if (select.value !== favPhone) deviceChanged = true;
          select.value = favPhone;
        }
      }
      const choicesInstance = new (window as any).Choices(select, {
        shouldSort: false,
        shouldSortItems: false,
      });
      (select as any)._choices = choicesInstance;
    });

    // Pre-select case type button from cookie — only if available for current device
    const favCaseType = this.getCookie("favCaseType");
    if (favCaseType) {
      const decoded = decodeURIComponent(favCaseType);
      const currentDevice = this.querySelector<HTMLSelectElement>('[name="options[Device]"], [name="options[Modèle]"]')?.value;

      const isAvailable = currentDevice ? this.getVariantData().some((v) => v.option1 === currentDevice && v.option2 === decoded) : false;

      if (isAvailable) {
        const btn = this.querySelector<HTMLButtonElement>(`.variants-case .button-toggle[value="${CSS.escape(decoded)}"]`);
        if (btn) {
          this.querySelectorAll<HTMLButtonElement>(".variants-case .button-toggle").forEach((b) => (b.dataset.selected = "false"));
          btn.dataset.selected = "true";
        }
      }
    }

    // When the cookie-selected device differs from the server-rendered variant, the server
    // rendered kits, input[name=id] and Case Type button availability are wrong. Dispatch the
    // change event on the device <select> itself (not `this`) so onPhoneVariantChange() — which
    // reads event.target.name to recognize a device change — actually re-disables Case Type
    // buttons for the new device, in addition to onVariantChange() re-resolving the variant.
    if (deviceChanged) {
      const deviceSelect = this.querySelector<HTMLSelectElement>('[name="options[Device]"], [name="options[Modèle]"]');
      deviceSelect?.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  private getCookie(name: string): string | undefined {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  }

  /** Returns the available case-type options for the currently selected phone model. */
  getVariantOptions(): VariantOption[] {
    const phoneVariant = this.querySelector<HTMLSelectElement>('[name="options[Device]"], [name="options[Modèle]"], [name="options[Size]"]')?.value;
    if (!phoneVariant) return [];

    return this.getVariantData()
      .filter((v) => v.option1 === phoneVariant)
      .map((v) => ({ label: v.options[1], value: v.options[1] }));
  }

  /** Reads Device select + selected case type button + MagSafe radio to build options array.
   *  Falls back to custom addon radio inputs (ChargePods / AirGrips). */
  updateOptions(): void {
    const deviceSelect = this.querySelector<HTMLSelectElement>('[name="options[Device]"], [name="options[Modèle]"], [name="options[Size]"]');
    const deviceValue = deviceSelect?.value ?? "";
    const selectedCaseTypeBtn = this.querySelector<HTMLButtonElement>('.variants-case .button-toggle[data-selected="true"], .variants-case .button-toggle.active');
    const magsafeFieldset = this.querySelector<HTMLElement>('[data-option-name="magsafe-ring"]');
    const magsafeValue = magsafeFieldset?.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.value ?? "";

    // Custom products: Design Style radios (option1) + Case Type button (option2), no Device.
    // Insert the design style at its own position — the device-first ordering below would
    // shift it to the end and no variant would match.
    const designStyleFieldset = this.querySelector<HTMLElement>('[data-option-name="design-style"]');
    if (designStyleFieldset) {
      const designStyleValue = designStyleFieldset.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.value ?? "";
      const others: string[] = [];
      if (selectedCaseTypeBtn?.value) others.push(selectedCaseTypeBtn.value);
      this.querySelectorAll<HTMLSelectElement>("select").forEach((s) => { if (s.value) others.push(s.value); });
      if (magsafeValue) others.push(magsafeValue);
      const position = parseInt(designStyleFieldset.dataset.optionPosition ?? "1") || 1;
      others.splice(position - 1, 0, designStyleValue);
      this.options = others;
      return;
    }

    if (deviceValue || selectedCaseTypeBtn) {
      const nonDeviceSelects = Array.from(this.querySelectorAll<HTMLSelectElement>("select"))
        .filter((s) => s !== deviceSelect);

      // Build option2+ values in product-option order:
      // - Case-type button first (always option2 in KaseMe products: Impact, Finish…)
      // - Then any remaining non-device selects (option3+, e.g. Color on AirPod Cases,
      //   Bumper Color on Clear Cases when no button is present)
      const option2Plus: string[] = [];
      if (selectedCaseTypeBtn?.value) option2Plus.push(selectedCaseTypeBtn.value);
      nonDeviceSelects.forEach((s) => { if (s.value) option2Plus.push(s.value); });

      const base = option2Plus.length > 0 ? [deviceValue, ...option2Plus] : [deviceValue];
      this.options = magsafeValue ? [...base, magsafeValue] : base;
      return;
    }

    // ChargePods / AirGrips: radio buttons rendered by variant-chargepod-case.liquid
    const addonRadios = this.querySelectorAll<HTMLInputElement>(".variant-custom-addon__input:checked");
    if (addonRadios.length > 0) {
      this.options = Array.from(addonRadios, (r) => r.value);
      return;
    }

    // Fallback for products with no Device/Modèle select and no case-type buttons
    // (e.g. gift cards with "Amount", iPad Clear Case with "Model"). Read all selects
    // in DOM order so updateMasterId() can match variants normally instead of getting [""].
    const allSelects = Array.from(this.querySelectorAll<HTMLSelectElement>("select"));
    if (allSelects.length > 0) {
      this.options = allSelects.map((s) => s.value);
      return;
    }

    this.options = [deviceValue];
  }

  private onPhoneVariantChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const watchedNames = ["options[Device]", "options[Modèle]", "options[Bumper Color]", "options[Couleur]"];
    if (!watchedNames.includes(target.name)) return;

    // Persist device selection so it survives color-product navigation
    if (target.name === "options[Device]" || target.name === "options[Modèle]") {
      document.cookie = `favPhone=${target.value}; path=/; max-age=${60 * 60 * 24 * 14}`;

      // Enable/disable case type buttons for new device
      const selectedDevice = target.value;
      const caseTypeButtons = this.querySelectorAll<HTMLButtonElement>(".variants-case .button-toggle");
      caseTypeButtons.forEach((btn) => {
        const available = this.getVariantData().some((v) => v.option1 === selectedDevice && v.option2 === btn.value);
        btn.disabled = !available;
      });

      // If current selection is now disabled, switch to first available
      const currentBtn = this.querySelector<HTMLButtonElement>('.variants-case .button-toggle[data-selected="true"], .variants-case .button-toggle.active');
      if (!currentBtn || currentBtn.disabled) {
        const firstEnabled = this.querySelector<HTMLButtonElement>(".variants-case .button-toggle:not([disabled])");
        if (firstEnabled) {
          caseTypeButtons.forEach((b) => (b.dataset.selected = "false"));
          firstEnabled.dataset.selected = "true";
        }
      }
    }

    const options = this.getVariantOptions();
    if (!options.length) return;

    this.updateCaseTypeOptions(options);

    // Re-resolve currentVariant with updated case type buttons, then update media
    this.updateOptions();
    this.updateMasterId();
    this.updateMagsafeFieldset();
    this.updateMedia();

    // Sync color-variant links on related products list
    const option1 = this.currentVariant?.option1;
    document.querySelectorAll<HTMLAnchorElement>(".related-products__list .related-products__link").forEach((link) => {
      const variantId = option1 != null ? variantIdFor(link, option1) : undefined;
      if (variantId) {
        const base = link.href.split("?")[0];
        link.href = `${base}?variant=${variantId}`;
      }
    });
  }

  private listenToChanges(): void {
    this.addEventListener("change", this.onPhoneVariantChange.bind(this));
    this.addEventListener("change", () => this.swapKoboImages());

    // Color swatch listener is now initialized globally via initColorSwatchListener()

    // Case type button click → mark selected, save cookie, trigger variant change
    this.querySelectorAll<HTMLButtonElement>(".variants-case .button-toggle").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.querySelectorAll<HTMLButtonElement>(".variants-case .button-toggle").forEach((b) => (b.dataset.selected = "false"));
        btn.dataset.selected = "true";
        document.cookie = `favCaseType=${encodeURIComponent(btn.value)}; path=/; max-age=${60 * 60 * 24 * 14}`;
        this.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    this.querySelectorAll<HTMLElement>("[data-toggle-phone-model]").forEach((openBtn) => {
      const dialog = openBtn.parentElement?.querySelector<HTMLDialogElement>(".dialog");
      openBtn.addEventListener("click", () => {
        if (!dialog) return;
        document.body.classList.add("--no-scroll");
        dialog.showModal();
      });
    });

    this.querySelectorAll<HTMLElement>(".dialog [data-dialog-close]").forEach((closeBtn) => {
      closeBtn.addEventListener("click", () => {
        document.body.classList.remove("--no-scroll");
        (closeBtn.parentElement as HTMLDialogElement)?.close();
      });
    });
  }

  /**
   * Swaps lifestyle slides between their original (Kindle) image and their Kobo
   * replacement based on the selected variant. Slides opt in via the
   * data-og-image / data-remplacement-img attributes, rendered by
   * product-v2-visionneuse when the section's cc_* image settings are filled.
   * No-ops on product types with a color option and on pages without opted-in
   * slides, so phone and iPad cases are unaffected.
   */
  private swapKoboImages(): void {
    const slides = document.querySelectorAll<HTMLElement>("[data-og-image]");
    if (this.querySelector('[name="options[Bumper Color]"], [name="options[Couleur]"]') || slides.length === 0) return;

    const isKoboSelected = Array.from(this.querySelectorAll<HTMLSelectElement>("select")).some(
      (select) => select.value.toLowerCase().includes("kobo")
    );

    slides.forEach((slide) => {
      const img = slide.querySelector<HTMLImageElement>("img");
      if (!img?.parentElement) return;
      const replacement = slide.dataset.remplacementImg;
      const og = slide.dataset.ogImage;
      if (!replacement || !og) return;
      img.parentElement.innerHTML = img.parentElement.innerHTML.replaceAll(
        isKoboSelected ? og : replacement,
        isKoboSelected ? replacement : og
      );
    });
  }

  /**
   * Fetches the new color product via Section Rendering API, replaces the main
   * product section in the DOM (triggering custom-element re-init), and updates
   * the browser URL via pushState — no full page reload.
   * @deprecated use the module-level switchToProduct() instead
   */
  private switchToProduct(link: HTMLAnchorElement): Promise<void> {
    return switchToProduct(link);
  }

  /**
   * Rebuilds the case-type <select> with available options for the chosen phone,
   * then handles bumper-color image swapping and reward point updates.
   */
  updateCaseTypeOptions(options: VariantOption[], _initial = false): void {
    const caseTypeSelect = this.querySelector<HTMLSelectElement>('[name="options[Case Type]"], [name="options[Feature & Color]"], [name="options[Bumper Color]"], [name="options[Couleur]"]');
    if (!caseTypeSelect) return;

    const previousValue = caseTypeSelect.value;
    const newSelectedValue = options.some((o) => o.value === previousValue)
      ? previousValue
      : (options[0]?.value ?? '');

    const choicesInstance = (caseTypeSelect as any)._choices;
    if (choicesInstance) {
      choicesInstance.setChoices(
        options.map(({ label, value }) => ({ value, label, selected: value === newSelectedValue })),
        'value',
        'label',
        true,
      );
      if (options.length <= 1) choicesInstance.disable();
      else choicesInstance.enable();
    } else {
      caseTypeSelect.innerHTML = "";
      options.forEach(({ label, value }) => {
        const opt = document.createElement("option");
        opt.value = value;
        opt.textContent = label;
        opt.selected = value === newSelectedValue;
        caseTypeSelect.appendChild(opt);
      });
      caseTypeSelect.disabled = options.length <= 1;
    }

    // Bumper color image swap
    const bumperColorSelect = this.querySelector<HTMLSelectElement>('[name="options[Bumper Color]"], [name="options[Couleur]"]');
    if (!bumperColorSelect) return;

    const isClear = newSelectedValue.includes("Clear");
    document.querySelectorAll<HTMLElement>("[data-og-image]").forEach((bloc) => {
      const img = bloc.querySelector<HTMLImageElement>("img");
      if (!img) return;
      const og = bloc.dataset.ogImage ?? "";
      const replacement = bloc.dataset.remplacementImg ?? "";
      const [from, to] = isClear ? [og, replacement] : [replacement, og];
      img.parentElement!.innerHTML = img.parentElement!.innerHTML.replaceAll(from, to);
    });

    // Update reward points for current bumper-color variant
    const selects = this.querySelectorAll<HTMLSelectElement>("select");
    if (selects.length < 2) return;

    const currentVariant = this.getVariantData().find((v) => v.option1 === selects[0].value && v.option2 === selects[1].value);
    if (!currentVariant) return;

    const mention = document.querySelector<HTMLElement>(".km-reward-mention");
    if (!mention) return;

    let points = currentVariant.price / 100;
    if (String(currentVariant.price % 100).padStart(2, "0") !== "00") points += 1;
    points = Math.trunc(points) * 10;

    const em = mention.querySelector("em");
    if (em) em.innerHTML = `<strong>${points} points</strong>`;
  }

  renderProductInfo(): void {
    this.updateMagsafeFieldset();
    super.renderProductInfo();
  }

  /** Updates MagSafe fieldset: availability per radio, show/hide, and legend text. */
  private updateMagsafeFieldset(): void {
    const magsafeFieldset = this.querySelector<HTMLElement>('[data-option-name="magsafe-ring"]');
    if (!magsafeFieldset) return;

    const magsafePos = parseInt(magsafeFieldset.dataset.optionPosition ?? "0") || 1;
    const magsafeRadios = magsafeFieldset.querySelectorAll<HTMLInputElement>('input[type="radio"]');
    let existingValueCount = 0;

    magsafeRadios.forEach((radio) => {
      const optionKeys = ["option1", "option2", "option3"] as const;
      const magsafeKey = optionKeys[magsafePos - 1];
      const currentDevice = this.currentVariant?.option1;

      // Existence (drives row visibility below): scoped to the selected DEVICE
      // only, ignoring Case Type/Finish — a value that ships under a different
      // Finish for the same device still counts. Mirrors Liquid's
      // magsafe_existing_count in product-variant-picker-custom.liquid — keep both
      // in sync. Scoping this to the full combo used to hide the row whenever the
      // current Finish happened not to carry both values, even though another
      // Finish for the same device does.
      const existsForDevice = this.getVariantData().some(
        (variant) => (!currentDevice || variant.option1 === currentDevice) && variant[magsafeKey] === radio.value,
      );

      // Availability (drives this radio's disabled state): scoped to the exact
      // current combination, since picking this value must resolve to a real,
      // in-stock variant for the Case Type/Finish currently selected.
      const matchesCurrentCombo = (variant: ShopifyVariant): boolean => {
        for (let i = 1; i <= 3; i++) {
          if (i === magsafePos) continue;
          const key = optionKeys[i - 1];
          // Compare against what the customer selected, not currentVariant: when the checked
          // MagSafe value doesn't exist for a new device, updateMasterId() falls back to that
          // device's first variant, which can carry another Case Type.
          const currentVal = this.options[i - 1] ?? this.currentVariant?.[key];
          if (currentVal && variant[key] !== currentVal) return false;
        }
        return variant[magsafeKey] === radio.value;
      };
      const isAvailable = this.getVariantData().some((variant) => variant.available && matchesCurrentCombo(variant));

      radio.disabled = !isAvailable;
      if (existsForDevice) existingValueCount++;
    });

    const variantsWrapper = magsafeFieldset.closest<HTMLElement>(".variants");

    // Hide the row only when the current device has a single MagSafe Ring value
    // that actually exists as a variant (in any Case Type/Finish) — not when a
    // value merely happens to be out of stock right now (it stays visible,
    // disabled, so the customer can see the choice exists).
    variantsWrapper?.classList.toggle("hidden", existingValueCount <= 1);

    // If the currently checked option is no longer available, select the first available one.
    const checkedRadio = magsafeFieldset.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    if (checkedRadio?.disabled) {
      const firstAvailable = magsafeFieldset.querySelector<HTMLInputElement>('input[type="radio"]:not([disabled])');
      if (firstAvailable) {
        firstAvailable.checked = true;
        this.updateOptions();
        this.updateMasterId();
        if (this.currentVariant) {
          this.updateURL();
          this.updateVariantInput();
        }
      }
    }

    // Update option label + popup title: Pixel device → "Pixel Snap Ring", others → "MagSafe Ring"
    const deviceSelect = this.querySelector<HTMLSelectElement>('[name="options[Device]"], [name="options[Modèle]"]');
    const isPixel = deviceSelect?.value?.toLowerCase().includes("pixel") ?? false;

    const optionLabel = variantsWrapper?.querySelector<HTMLElement>(".variants-header--title");
    if (optionLabel) {
      optionLabel.textContent = isPixel
        ? (magsafeFieldset.dataset.legendPixel ?? "")
        : (magsafeFieldset.dataset.legendDefault ?? "");
    }

    const popup = variantsWrapper?.querySelector<HTMLElement>("[data-phone-info]");
    if (popup) {
      popup.querySelector<HTMLElement>("[data-iphone]")?.toggleAttribute("hidden", isPixel);
      popup.querySelector<HTMLElement>("[data-pixel]")?.toggleAttribute("hidden", !isPixel);
    }
  }
}
customElements.define("variant-selects-case-type-filter", VariantSelectsCaseTypeFilter);

// ============================================================
// Color swatch AJAX navigation — standalone (always initialized)
// ============================================================

async function switchToProduct(link: HTMLAnchorElement): Promise<void> {
  const mainEl = document.querySelector<HTMLElement>("product-info[data-section]");
  const sectionId = mainEl?.dataset.section ?? "";
  if (!sectionId) {
    window.location.href = link.href;
    return;
  }

  const url = new URL(link.href);
  const fetchUrl = `${url.pathname}${url.search}&sections=${sectionId}`;

  try {
    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const sections = (await res.json()) as Record<string, string>;
    const sectionHtml = sections[sectionId];
    if (!sectionHtml) throw new Error("No section HTML returned");

    const parsed = new DOMParser().parseFromString(sectionHtml, "text/html");
    const newSection = parsed.getElementById(`MainProduct-${sectionId}`);
    const currentSection = document.getElementById(`MainProduct-${sectionId}`);
    if (!newSection || !currentSection) throw new Error("MainProduct element not found");

    window.history.pushState({}, "", link.href);
    currentSection.replaceWith(newSection);

    // Bypass scroll-trigger animations on newly inserted elements
    newSection.querySelectorAll<HTMLElement>(".scroll-trigger").forEach((el) => el.classList.add("scroll-trigger--cancel"));

    // Signal other modules (e.g. upsell-viewer) that the main section was swapped
    document.dispatchEvent(new CustomEvent("product:section-replaced"));

    // Also update product-side section (kits + variant picker) for the new product
    const sideEl = document.querySelector<HTMLElement>(".kits-container, variant-selects-case-type-filter");
    const sideSectionId = sideEl?.closest<HTMLElement>('[id^="shopify-section-"]')?.id.replace("shopify-section-", "");
    if (sideSectionId) {
      fetch(`${url.pathname}${url.search}&sections=${sideSectionId}`)
        .then((r) => r.json() as Promise<Record<string, string>>)
        .then((data) => {
          const sideHtml = data[sideSectionId];
          if (!sideHtml) return;
          const sideWrapper = document.getElementById(`shopify-section-${sideSectionId}`);
          if (!sideWrapper) return;
          const sideParsed = new DOMParser().parseFromString(sideHtml, "text/html");
          const newSideWrapper = sideParsed.getElementById(`shopify-section-${sideSectionId}`);
          if (newSideWrapper) sideWrapper.replaceWith(newSideWrapper);
        });
    }
  } catch (err) {
    console.error("[switchToProduct] fallback:", err);
    window.location.href = link.href;
  }
}

export function initColorSwatchListener(): void {
  if (document.body.dataset.colorSwatchListenerAttached) return;
  document.body.dataset.colorSwatchListenerAttached = "true";
  document.addEventListener("click", (e) => {
    const link = (e.target as HTMLElement).closest<HTMLAnchorElement>(".related-products__link");
    if (!link || link.classList.contains("selected")) return;
    e.preventDefault();
    switchToProduct(link);
  });
}

interface RelatedProductJson {
  title: string;
  url: string;
  handle: string;
  featured_image: string | null;
  variants: Array<{ id: number; option1: string | null; available: boolean; featured_image: { src: string } | null }>;
}

/**
 * Fetches and appends the colors that couldn't be rendered server-side because
 * they sit past Shopify's 20-object `all_products[handle]` limit (see the
 * comment in product-colors.liquid). Reads the handles + matching context off
 * `.related-products__list[data-overflow-refs]`, so it's a no-op on any list
 * that stayed within the limit.
 */
export function hydrateRelatedProductsOverflow(): void {
  document.querySelectorAll<HTMLUListElement>(".related-products__list[data-overflow-refs]").forEach((list) => {
    if (list.dataset.overflowHydrated) return;
    list.dataset.overflowHydrated = "true";

    let handles: string[] = [];
    try {
      handles = JSON.parse(list.dataset.overflowRefs ?? "[]");
    } catch {
      return;
    }
    if (!handles.length) return;

    const context = {
      currentHandle: list.dataset.currentHandle ?? "",
      productType: list.dataset.productType ?? "",
      currentOption1: list.dataset.currentOption1 ?? "",
    };

    handles.forEach((handle) => {
      fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? (res.json() as Promise<RelatedProductJson>) : null))
        .then((product) => {
          if (!product) return;
          const li = buildRelatedProductItem(product, context);
          if (li) list.appendChild(li);
        })
        .catch(() => {
          // One missing colorway shouldn't break the rest of the swatch list.
        });
    });
  });
}

/** Mirrors the variant/image resolution in product-colors.liquid's for-loop. */
function buildRelatedProductItem(
  product: RelatedProductJson,
  context: { currentHandle: string; productType: string; currentOption1: string }
): HTMLLIElement | null {
  let variantId: number | null = product.variants.find((v) => v.available)?.id ?? null;
  let image = product.featured_image;

  if (context.productType !== "AirGrip" && context.currentOption1) {
    const match = product.variants.find((v) => v.option1 === context.currentOption1);
    if (match) {
      variantId = match.id;
      if (match.featured_image?.src) image = match.featured_image.src;
    }
  }
  if (!image) return null;

  const href = context.productType !== "AirGrip"
    ? (variantId ? `${product.url}?variant=${variantId}` : null)
    : product.url;
  if (!href) return null;

  const li = document.createElement("li");
  li.className = "related-products__container";

  const a = document.createElement("a");
  a.className = `related-products__link${product.handle === context.currentHandle ? " selected" : ""}`;
  a.href = href;
  a.dataset.variantMap = JSON.stringify(buildVariantMap(product.variants));

  const imageContainer = document.createElement("div");
  imageContainer.className = "related-products__image-container";

  const img = document.createElement("img");
  img.src = `${image}${image.includes("?") ? "&" : "?"}width=240`;
  img.alt = `${product.title} -`;
  img.className = "related-products__image";
  img.loading = "lazy";

  imageContainer.appendChild(img);
  a.appendChild(imageContainer);
  li.appendChild(a);
  return li;
}
